import { INQUIRY_HONEYPOT_FIELD } from './validateInquiry';
import type { InquiryTypeId } from './inquiryTypes';

export type SubmitInquiryInput = {
  type: InquiryTypeId;
  name: string;
  email: string;
  message: string;
  company?: string;
  phone?: string;
  source?: string;
  locale?: string;
  /** Honeypot (`inquiry_ref_alt`) — must be empty for real submissions. */
  inquiry_ref_alt?: string;
};

export type SubmitInquiryResult =
  | { ok: true; messageId?: string }
  | { ok: false; error: string };

/**
 * POST target for inquiry forms. Dev: Next `route.ts`. Hostinger static: PHP in `out/api/inquiries/`.
 * `trailingSlash: true` redirects `/api/inquiries` → `/api/inquiries/` (308) — keep the trailing slash.
 */
export function getInquiryApiUrl(): string {
  const base = process.env.NEXT_PUBLIC_CONTACT_API_URL?.replace(/\/$/, '');
  if (base) return `${base}/api/inquiries/`;
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api/inquiries/`;
  }
  return '/api/inquiries/';
}

export async function submitInquiry(input: SubmitInquiryInput): Promise<SubmitInquiryResult> {
  const url = getInquiryApiUrl();

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        type: input.type,
        name: input.name,
        email: input.email,
        message: input.message,
        company: input.company,
        phone: input.phone,
        source: input.source,
        locale: input.locale,
        [INQUIRY_HONEYPOT_FIELD]: input.inquiry_ref_alt ?? '',
      }),
    });

    const payload = (await res.json().catch(() => ({}))) as {
      error?: string;
      messageId?: string;
      ok?: boolean;
    };

    if (!res.ok) {
      return { ok: false, error: payload.error ?? `Request failed (${res.status})` };
    }

    if (payload.ok === false) {
      return { ok: false, error: payload.error ?? 'Request failed' };
    }

    return { ok: true, messageId: payload.messageId };
  } catch (err) {
    const hint =
      typeof window !== 'undefined' && !process.env.NEXT_PUBLIC_CONTACT_API_URL
        ? ' Is the dev server running (npm run dev)? Static hosting cannot run /api without NEXT_PUBLIC_CONTACT_API_URL.'
        : '';
    const detail = err instanceof Error ? err.message : 'Network error';
    return { ok: false, error: `${detail}${hint}` };
  }
}
